import React, { useState } from 'react';
import {
  Chip,
  Fade,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import _ from 'lodash';

import {
  IConversation,
  IConversationUser,
} from '../../../../../store/V2/ChatSlice';
import { useAppSelector } from '../../../../../hooks';
import { TP_COLORS } from '../../../../../constants';
import { getAuthState } from '../../../../../store/AuthSlice';
import { getConversationAvatar, getConversationName } from '../../chatHelpers';
import { getAdminChatState } from '../../../../../store/V2/AdminChatSlice';
import { TPTextField } from '../../../../../components/Inputs';
import BasicLoader from '../../../../../components/Loaders/BasicLoader';

interface AdminChatConversationsViewProps {
  onConversationSelect: (conversation: IConversation) => void;
}

const filterConversationList = (
  conversation: IConversation,
  authUserId: string,
  searchText: string
) => {
  const users = conversation.users as IConversationUser[];

  if (users.length === 0) return true;

  const otherUsers = users.filter((u) => u._id !== authUserId);

  return otherUsers.some((item) =>
    `${item.firstname} ${item.lastname}`
      .toLowerCase()
      .includes(searchText.toLowerCase())
  );
};

const AdminChatConversationsView: React.FunctionComponent<AdminChatConversationsViewProps> = ({
  onConversationSelect,
}) => {
  // Store
  const authState = useAppSelector(getAuthState);
  const adminChatState = useAppSelector(getAdminChatState);

  const [searchText, setSearchText] = useState('');

  const { adminChatConversationsData } = adminChatState;

  const authUser = authState.user;
  let conversationList = [
    ...adminChatConversationsData.list,
  ].filter((conversation) =>
    filterConversationList(conversation, authUser?._id || '', searchText)
  );

  conversationList = _.orderBy(
    [...conversationList],
    ['lastMessageTime', 'unseenCount'],
    ['desc', 'desc']
  );

  return (
    <Fade in>
      <div>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            backgroundColor: TP_COLORS.listItemGray.light,
            borderRadius: '28px',
            padding: '8px',
            margin: '8px',
          }}
        >
          <SearchIcon
            fontSize="small"
            sx={{ padding: '5px', color: TP_COLORS.gray.darken }}
          />
          <TPTextField
            multiline
            fullWidth
            placeholder="Search"
            variant="standard"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              disableUnderline: true,
              sx: {
                fontSize: '14px',
              },
            }}
          />
        </Stack>
        {adminChatConversationsData.isLoading ? (
          <BasicLoader
            loaderProps={{ size: 30 }}
            containerProps={{ height: '300px', alignItems: 'center' }}
          />
        ) : null}
        {!adminChatConversationsData.isLoading ? (
          <List sx={{ overflowY: 'auto' }}>
            {conversationList.map((conversation) => {
              return (
                <ListItemButton
                  key={conversation._id}
                  onClick={() => onConversationSelect(conversation)}
                >
                  <ListItemAvatar
                    sx={{
                      m: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      width: '80px',
                    }}
                  >
                    {getConversationAvatar(conversation, authUser?._id)}
                  </ListItemAvatar>
                  <ListItemText
                    sx={{ pl: '5px' }}
                    primary={getConversationName(conversation, authUser?._id)}
                  />
                  {conversation.unSeenCount && conversation.unSeenCount > 0 ? (
                    <Chip
                      size="small"
                      label={conversation.unSeenCount}
                      sx={{
                        backgroundColor: '#dd2334',
                        color: TP_COLORS.white,
                      }}
                    />
                  ) : null}
                </ListItemButton>
              );
            })}
          </List>
        ) : null}
      </div>
    </Fade>
  );
};

export default AdminChatConversationsView;
